import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Publish as PostIcon,
  Unpublished as UnpostIcon,
  Warning as WarningIcon,
  CheckCircleOutline as SuccessIcon,
  RemoveCircleOutline as UnapproveIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import StockCountDialog from '../components/stockCounts/StockCountDialog';
import StockCountPreviewDialog from '../components/stockCounts/StockCountPreviewDialog';
import { StockCount } from '../types/stockCounts.types';
import { stockCountsService } from '../services/stockCounts.service';

const translations = {
  en: {
    title: 'Stock Counting',
    addStockCount: 'New Stock Count',
    countNumber: 'Count Number',
    countDate: 'Count Date',
    location: 'Location',
    countType: 'Type',
    itemsCount: 'Items',
    status: 'Status',
    actions: 'Actions',
    search: 'Search by count number...',
    noData: 'No stock counts found',
    inProgress: 'In Progress',
    completed: 'Completed',
    approved: 'Approved',
    posted: 'Posted',
    edit: 'Edit',
    delete: 'Delete',
    approve: 'Approve',
    unapprove: 'Unapprove',
    post: 'Post',
    unpost: 'Unpost',
    variance: 'Variance',
    totalCounts: 'Total Counts',
    pendingApproval: 'Pending Approval',
    totalVariance: 'Total Variance',
    refresh: 'Refresh',
    cancel: 'Cancel',
    confirm: 'Confirm',
    confirmDeleteMsg: 'Are you sure you want to delete stock count',
    confirmApproveMsg: 'Are you sure you want to approve stock count',
    confirmUnapproveMsg: 'Are you sure you want to unapprove stock count',
    confirmPostMsg: 'Are you sure you want to post stock count',
    confirmUnpostMsg: 'Are you sure you want to unpost stock count',
    confirmDeleteNote: 'This action cannot be undone.',
    confirmApproveNote: 'Stock levels will be updated and a journal entry will be created.',
    confirmUnapproveNote: 'Stock levels will be restored to system quantities and the journal entry will be deleted.',
    confirmPostNote: 'General ledger entries will be created.',
    confirmUnpostNote: 'General ledger entries will be removed.',
    successDelete: 'Stock count deleted successfully.',
    successApprove: 'Stock count approved successfully.',
    successUnapprove: 'Stock count approval reversed successfully.',
    successPost: 'Stock count posted successfully.',
    successUnpost: 'Stock count unposted successfully.',
    errorAction: 'An error occurred. Please try again.',
  },
  ar: {
    title: 'الجرد الدوري',
    addStockCount: 'جرد جديد',
    countNumber: 'رقم الجرد',
    countDate: 'تاريخ الجرد',
    location: 'الموقع',
    countType: 'النوع',
    itemsCount: 'الأصناف',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'البحث برقم الجرد...',
    noData: 'لا توجد جرد',
    inProgress: 'قيد التنفيذ',
    completed: 'مكتمل',
    approved: 'معتمد',
    posted: 'مرحّل',
    edit: 'تعديل',
    delete: 'حذف',
    approve: 'اعتماد',
    unapprove: 'إلغاء الاعتماد',
    post: 'ترحيل',
    unpost: 'إلغاء الترحيل',
    variance: 'الفرق',
    totalCounts: 'إجمالي الجرد',
    pendingApproval: 'بانتظار الاعتماد',
    totalVariance: 'إجمالي الفروقات',
    refresh: 'تحديث',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    confirmDeleteMsg: 'هل أنت متأكد من حذف الجرد',
    confirmApproveMsg: 'هل أنت متأكد من اعتماد الجرد',
    confirmUnapproveMsg: 'هل أنت متأكد من إلغاء اعتماد الجرد',
    confirmPostMsg: 'هل أنت متأكد من ترحيل الجرد',
    confirmUnpostMsg: 'هل أنت متأكد من إلغاء ترحيل الجرد',
    confirmDeleteNote: 'لا يمكن التراجع عن هذا الإجراء.',
    confirmApproveNote: 'سيتم تحديث مستويات المخزون وإنشاء قيد محاسبي.',
    confirmUnapproveNote: 'سيتم استعادة مستويات المخزون إلى الكميات النظامية وحذف القيد المحاسبي.',
    confirmPostNote: 'سيتم إنشاء قيود الأستاذ العام.',
    confirmUnpostNote: 'سيتم حذف قيود الأستاذ العام.',
    successDelete: 'تم حذف الجرد بنجاح.',
    successApprove: 'تم اعتماد الجرد بنجاح.',
    successUnapprove: 'تم إلغاء اعتماد الجرد بنجاح.',
    successPost: 'تم ترحيل الجرد بنجاح.',
    successUnpost: 'تم إلغاء ترحيل الجرد بنجاح.',
    errorAction: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
  },
};

type ConfirmAction = 'delete' | 'approve' | 'unapprove' | 'post' | 'unpost';

interface ConfirmDialogState {
  open: boolean;
  action: ConfirmAction | null;
  stockCount: StockCount | null;
}

const StyledDialogTitle = styled(DialogTitle)<{ gradientFrom: string; gradientTo: string }>(
  ({ gradientFrom, gradientTo }) => ({
    background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 24px',
  })
);

const StockCountsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const { userBranchIds, userWarehouseIds } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const keeperClaims = payload.KeeperWarehouseId;
      const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
      return { userBranchIds: branchIds, userWarehouseIds: warehouseIds };
    } catch {
      return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
    }
  })();

  const canEditStockCount = (stockCount: StockCount) => {
    if (userBranchIds.length === 0 && userWarehouseIds.length === 0) return true;
    if (stockCount.warehouseId && userWarehouseIds.includes(stockCount.warehouseId)) return true;
    if (stockCount.branchId && userBranchIds.includes(stockCount.branchId)) return true;
    return false;
  };

  const canApproveStockCount = () => {
    return userBranchIds.length === 0 && userWarehouseIds.length === 0;
  };

  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  const [stockCounts, setStockCounts] = useState<StockCount[]>([]);
  const [filteredStockCounts, setFilteredStockCounts] = useState<StockCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStockCount, setSelectedStockCount] = useState<StockCount | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStockCount, setPreviewStockCount] = useState<StockCount | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    action: null,
    stockCount: null,
  });

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    totalVariance: 0,
  });

  useEffect(() => {
    loadStockCounts();
  }, []);

  // Auto-open stock count preview when ?openId=X is in the URL (used by Inventory Rebalance trace)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('openId');
    if (!openId) return;
    const id = parseInt(openId, 10);
    if (!Number.isFinite(id)) return;
    (async () => {
      try {
        const full = await stockCountsService.getById(id);
        setPreviewStockCount(full);
        setPreviewOpen(true);
      } catch {
        toast.error(t.errorAction);
      }
    })();
  }, []);

  useEffect(() => {
    filterStockCounts();
    calculateStats();
  }, [searchTerm, stockCounts]);

  const loadStockCounts = async () => {
    setLoading(true);
    try {
      const data = await stockCountsService.getAll();
      const filteredData = data
        .filter((sc: StockCount) => canEditStockCount(sc))
        .sort((a: StockCount, b: StockCount) => b.stockCountId - a.stockCountId);
      setStockCounts(filteredData);
    } catch (error) {
      console.error('Failed to load stock counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStockCounts = () => {
    if (!searchTerm.trim()) {
      setFilteredStockCounts(stockCounts);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredStockCounts(stockCounts.filter(sc => sc.countNumber.toLowerCase().includes(term)));
  };

  const calculateStats = () => {
    const total = stockCounts.length;
    const pending = stockCounts.filter(sc => sc.status === 'Completed').length;
    const totalVariance = stockCounts.reduce((sum, sc) =>
      sum + sc.details.reduce((dSum, d) => dSum + (d.varianceValue || 0), 0), 0);
    setStats({ total, pending, totalVariance });
  };

  const openConfirm = (action: ConfirmAction, sc: StockCount) => {
    setConfirmDialog({ open: true, action, stockCount: sc });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, action: null, stockCount: null });
  };

  const handleConfirm = async () => {
    const { action, stockCount } = confirmDialog;
    if (!action || !stockCount) return;
    closeConfirm();
    try {
      if (action === 'delete') {
        await stockCountsService.delete(stockCount.stockCountId);
        toast.success(t.successDelete);
      } else if (action === 'approve') {
        await stockCountsService.approve(stockCount.stockCountId);
        toast.success(t.successApprove);
      } else if (action === 'unapprove') {
        await stockCountsService.unapprove(stockCount.stockCountId);
        toast.success(t.successUnapprove);
      } else if (action === 'post') {
        await stockCountsService.post(stockCount.stockCountId);
        toast.success(t.successPost);
      } else if (action === 'unpost') {
        await stockCountsService.unpost(stockCount.stockCountId);
        toast.success(t.successUnpost);
      }
      loadStockCounts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  const handleAdd = () => {
    setSelectedStockCount(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (stockCount: StockCount) => {
    setSelectedStockCount(stockCount);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedStockCount(undefined);
  };

  const handleSave = () => {
    loadStockCounts();
  };

  const handleView = async (sc: StockCount) => {
    try {
      const full = await stockCountsService.getById(sc.stockCountId);
      setPreviewStockCount(full);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Failed to load stock count details:', error);
      toast.error(t.errorAction);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'InProgress': return 'default';
      case 'Completed': return 'warning';
      case 'Approved': return 'success';
      case 'Posted': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'InProgress': return t.inProgress;
      case 'Completed': return t.completed;
      case 'Approved': return t.approved;
      case 'Posted': return t.posted;
      default: return status || '-';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const getTotalVariance = (sc: StockCount) =>
    sc.details.reduce((sum, d) => sum + (d.varianceValue || 0), 0);

  const getLocation = (sc: StockCount) => {
    if (sc.branchId) return isRTL ? sc.branchNameAr : sc.branchName;
    if (sc.warehouseId) return isRTL ? sc.warehouseNameAr : sc.warehouseName;
    return '-';
  };

  const confirmConfig: Record<ConfirmAction, {
    gradientFrom: string;
    gradientTo: string;
    icon: React.ReactNode;
    buttonColor: 'error' | 'success' | 'primary' | 'warning';
    message: string;
    note: string;
    buttonLabel: string;
  }> = {
    delete: {
      gradientFrom: '#e53e3e',
      gradientTo: '#c53030',
      icon: <WarningIcon sx={{ fontSize: 28 }} />,
      buttonColor: 'error',
      message: t.confirmDeleteMsg,
      note: t.confirmDeleteNote,
      buttonLabel: t.delete,
    },
    approve: {
      gradientFrom: '#38a169',
      gradientTo: '#276749',
      icon: <SuccessIcon sx={{ fontSize: 28 }} />,
      buttonColor: 'success',
      message: t.confirmApproveMsg,
      note: t.confirmApproveNote,
      buttonLabel: t.approve,
    },
    unapprove: {
      gradientFrom: '#718096',
      gradientTo: '#4a5568',
      icon: <UnapproveIcon sx={{ fontSize: 28 }} />,
      buttonColor: 'warning',
      message: t.confirmUnapproveMsg,
      note: t.confirmUnapproveNote,
      buttonLabel: t.unapprove,
    },
    post: {
      gradientFrom: '#667eea',
      gradientTo: '#764ba2',
      icon: <PostIcon sx={{ fontSize: 28 }} />,
      buttonColor: 'primary',
      message: t.confirmPostMsg,
      note: t.confirmPostNote,
      buttonLabel: t.post,
    },
    unpost: {
      gradientFrom: '#ed8936',
      gradientTo: '#c05621',
      icon: <UnpostIcon sx={{ fontSize: 28 }} />,
      buttonColor: 'warning',
      message: t.confirmUnpostMsg,
      note: t.confirmUnpostNote,
      buttonLabel: t.unpost,
    },
  };

  const activeConfig = confirmDialog.action ? confirmConfig[confirmDialog.action] : null;

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(33.33% - 11px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{t.totalCounts}</Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(33.33% - 11px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.pending}</Typography>
                  <Typography variant="body2">{t.pendingApproval}</Typography>
                </Box>
                <ScheduleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(33.33% - 11px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: stats.totalVariance >= 0 ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.totalVariance.toFixed(2)}</Typography>
                  <Typography variant="body2">{t.totalVariance}</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Header Section */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadStockCounts}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
              }}
            >
              {t.addStockCount}
            </Button>
          </Stack>
        </Box>

        <TextField
          fullWidth
          placeholder={t.search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.countNumber}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.countDate}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.countType}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.location}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.itemsCount}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.variance}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStockCounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStockCounts.map((sc) => (
                  <TableRow key={sc.stockCountId} hover sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}>
                    <TableCell>
                      <Typography fontWeight="bold" color="#667eea">{sc.countNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="div">{formatDate(sc.countDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={sc.countType} size="small" variant="outlined" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="div" fontWeight="500">{getLocation(sc)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={sc.details.length}
                        size="small"
                        sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color={getTotalVariance(sc) >= 0 ? 'success.main' : 'error.main'}>
                        {getTotalVariance(sc).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={getStatusLabel(sc.status)}
                          color={getStatusColor(sc.status)}
                          size="small"
                          sx={{ fontWeight: 'bold', minWidth: 90 }}
                        />
                        {sc.journalEntryId && (
                          <Chip
                            label={`${sc.journalEntryNumber || 'JE'} (#${sc.journalEntryId})`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={isRTL ? 'معاينة' : 'Preview'}>
                          <IconButton
                            size="small"
                            onClick={() => handleView(sc)}
                            sx={{ color: '#00695c', '&:hover': { bgcolor: 'rgba(0,105,92,0.1)' } }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEditStockCount(sc) && (
                          <Tooltip title={t.edit}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(sc)}
                                disabled={sc.status === 'Approved' || sc.status === 'Posted'}
                                sx={{ color: 'primary.main', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canEditStockCount(sc) && (
                          <Tooltip title={t.delete}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openConfirm('delete', sc)}
                                disabled={
                                  userRoles.some(r => r === 'Super Admin')
                                    ? false
                                    : sc.status !== 'InProgress'
                                }
                                sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canApproveStockCount() && (
                          <Tooltip title={t.approve}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openConfirm('approve', sc)}
                                disabled={sc.status !== 'Completed'}
                                sx={{ color: 'success.main', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.1)' } }}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canApproveStockCount() && (
                          <Tooltip title={t.unapprove}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openConfirm('unapprove', sc)}
                                disabled={sc.status !== 'Approved'}
                                sx={{ color: '#718096', '&:hover': { bgcolor: 'rgba(113, 128, 150, 0.1)' } }}
                              >
                                <UnapproveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canPost && (
                          <Tooltip title={t.post}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openConfirm('post', sc)}
                                disabled={sc.status !== 'Approved'}
                                sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' } }}
                              >
                                <PostIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canPost && (
                          <Tooltip title={t.unpost}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openConfirm('unpost', sc)}
                                disabled={sc.status !== 'Posted'}
                                sx={{ color: 'warning.main', '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.1)' } }}
                              >
                                <UnpostIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Confirm Dialog */}
      {activeConfig && (
        <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
          <StyledDialogTitle gradientFrom={activeConfig.gradientFrom} gradientTo={activeConfig.gradientTo}>
            {activeConfig.icon}
            <Typography variant="h6" component="span" fontWeight="bold">
              {activeConfig.buttonLabel}
            </Typography>
          </StyledDialogTitle>
          <DialogContent sx={{ pt: 3, pb: 1 }}>
            <Typography variant="body1" gutterBottom>
              {activeConfig.message}{' '}
              <strong>{confirmDialog.stockCount?.countNumber}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {activeConfig.note}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={closeConfirm} variant="outlined" color="inherit">
              {t.cancel}
            </Button>
            <Button onClick={handleConfirm} variant="contained" color={activeConfig.buttonColor}>
              {activeConfig.buttonLabel}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <StockCountDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        stockCount={selectedStockCount}
        onSave={handleSave}
      />

      <StockCountPreviewDialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewStockCount(null); }}
        stockCount={previewStockCount}
        onEdit={() => {
          setPreviewOpen(false);
          if (previewStockCount) {
            setSelectedStockCount(previewStockCount);
            setDialogOpen(true);
          }
        }}
      />
    </Box>
  );
};

export default StockCountsPage;
